// Custom URL adapter — fetches rides from a user-supplied URL via GET.
// Expects `{ tours: [...] }` in the same JSON shape the file import accepts.
// URL is persisted in meta so auto-sync at page load re-fetches it.

import { getMeta, setMeta } from '../db.js';
import { normaliseRides } from './normaliseRides.js';
import { reportError } from '../telemetry.js';

const URL_META_KEY = 'customUrl_url';

const customUrlAdapter = {
    id: 'custom-url',
    name: 'Custom URL',
    kind: 'sync',
    description: 'Fetch rides from any URL that returns the ride JSON format',
    infoText: 'Fetch rides from any URL that returns JSON in the expected format. The endpoint must be publicly reachable (CORS-enabled) and return a { "tours": [...] } payload. Re-fetched each time the app opens — new rides only.',

    async renderConfig(container, onComplete, onDisconnect) {
        this._onDisconnect = onDisconnect;
        const savedUrl = await getMeta(URL_META_KEY);
        const lastOp = await getMeta('lastOp_custom-url');
        const statusText = lastOp
            ? `${lastOp.message} · ${new Date(lastOp.timestamp).toLocaleString()}`
            : '';

        if (!savedUrl) {
            container.innerHTML = `
                <input type="url" id="custom-url-input" class="url-input"
                       placeholder="https://example.com/rides.json" autocomplete="off">
                <button id="btn-custom-url-fetch" class="btn-brut btn-brut--primary" type="button">
                    Save &amp; fetch
                </button>
                <div id="custom-url-result" class="hint"></div>
                <p class="hint">
                    Expected JSON: <code>{ "tours": [ { "date": "YYYY-MM-DD", "distance_km": 40, ... } ] }</code>.
                    Fields <code>id</code>, <code>source</code>, <code>title</code>,
                    <code>duration_seconds</code>, <code>speed_kmh</code>, <code>user_id</code> are optional.
                    Download <a href="data/demo.json" download>demo data</a> for full example response.
                </p>
                <div class="adapter-status" aria-live="polite">${statusText}</div>
            `;
            const input = container.querySelector('#custom-url-input');
            const button = container.querySelector('#btn-custom-url-fetch');
            const resultEl = container.querySelector('#custom-url-result');
            button.addEventListener('click', () =>
                this._saveAndFetch(input.value.trim(), resultEl, container, onComplete));
            input.addEventListener('keydown', e => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    button.click();
                }
            });
        } else {
            // A connected source that isn't active reads "Activate" — syncing it
            // makes it the active source (ADR-0005, D1).
            const isActiveSource = (await getMeta('activeAdapter')) === 'custom-url';
            container.innerHTML = `
                <div class="url-display"></div>
                <button id="btn-custom-url-sync" class="btn-brut btn-brut--primary adapter-action-sync" type="button">
                    ${isActiveSource ? 'Sync now' : 'Activate'}
                </button>
                <button id="btn-custom-url-disconnect" class="btn-brut" type="button">
                    Disconnect
                </button>
                <div class="adapter-status" aria-live="polite">${statusText}</div>
            `;
            // savedUrl is user-supplied — assign via DOM so markup in the URL
            // can't break out of the attribute/element and inject script.
            const urlDisplay = container.querySelector('.url-display');
            urlDisplay.textContent = savedUrl;
            urlDisplay.title = savedUrl;
            const statusEl = container.querySelector('.adapter-status');
            container.querySelector('#btn-custom-url-sync')
                .addEventListener('click', async () => {
                    try {
                        statusEl.textContent = 'Fetching...';
                        const result = await this.sync(msg => { statusEl.textContent = msg; });
                        onComplete(result);
                    } catch (e) {
                        reportError(e, { adapter: 'custom-url', op: 'sync-now' });
                        statusEl.textContent = `Fetch failed: ${e.message}`;
                    }
                });
            container.querySelector('#btn-custom-url-disconnect')
                .addEventListener('click', () => onDisconnect?.());
        }
    },

    async _saveAndFetch(url, resultEl, container, onComplete) {
        if (!url || !/^https?:\/\//i.test(url)) {
            resultEl.className = 'hint hint--error';
            resultEl.textContent = 'Error: enter an http(s) URL.';
            return;
        }
        resultEl.className = 'hint';
        resultEl.textContent = 'Fetching...';
        try {
            await setMeta(URL_META_KEY, url);
            const result = await this.sync(msg => { resultEl.textContent = msg; });
            resultEl.className = 'hint hint--success';
            resultEl.textContent = result.message;
            onComplete(result);
            await this.renderConfig(container, onComplete, this._onDisconnect);
        } catch (e) {
            reportError(e, { adapter: 'custom-url', op: 'save-and-fetch' });
            await setMeta(URL_META_KEY, null);
            resultEl.className = 'hint hint--error';
            resultEl.textContent = `Fetch failed: ${e.message}`;
        }
    },

    async sync(progressCb) {
        const url = await getMeta(URL_META_KEY);
        if (!url) throw new Error('No URL configured');

        progressCb?.('Fetching...');
        const resp = await fetch(url, { cache: 'no-store' });
        if (!resp.ok) {
            console.warn(`[custom-url] HTTP ${resp.status} fetching ${url}`);
            throw new Error(`HTTP ${resp.status}`);
        }

        let json;
        try {
            json = await resp.json();
        } catch (err) {
            console.warn('[custom-url] Invalid JSON response', err);
            throw new Error('Invalid JSON response', { cause: err });
        }
        if (!json || !Array.isArray(json.tours)) {
            console.warn('[custom-url] Response must contain a "tours" array', json);
            throw new Error('Response must contain a "tours" array');
        }

        const { valid, skipped } = normaliseRides(json.tours, { defaultSource: 'custom-url' });
        if (skipped.length > 0) {
            console.warn(`[custom-url] Skipped ${skipped.length} entr${skipped.length === 1 ? 'y' : 'ies'}`, skipped);
        }
        const rideWord = valid.length === 1 ? 'ride' : 'rides';
        const message = skipped.length > 0
            ? `Fetched ${valid.length} ${rideWord}. ${skipped.length} entr${skipped.length === 1 ? 'y' : 'ies'} skipped.`
            : `Fetched ${valid.length} ${rideWord}.`;
        return { rides: valid, message };
    },

    // Disconnect: forget the saved URL (settings clears activeAdapter + leaves
    // ride data). The connected view then falls back to the URL-input view.
    async revokeAccess() {
        await setMeta(URL_META_KEY, null);
    },

    cleanup() {}
};

export default customUrlAdapter;
